import { useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { useEffect, useCallback } from "react";
import { cachePerformance, CACHE_CONFIG } from "@/lib/cache";

interface OptimizedQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  cacheType?: 'static' | 'user' | 'product' | 'search' | 'analytics';
  trackPerformance?: boolean;
  backgroundRefresh?: boolean;
  optimisticKey?: string;
}

export function useOptimizedQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: OptimizedQueryOptions<T> = {}
) {
  const queryClient = useQueryClient();
  const {
    cacheType = 'product',
    trackPerformance = true,
    backgroundRefresh = false,
    optimisticKey,
    ...queryOptions
  } = options;

  // Get cache configuration based on type
  const getCacheConfig = useCallback(() => {
    switch (cacheType) {
      case 'static':
        return {
          staleTime: CACHE_CONFIG.STATIC_DATA,
          gcTime: CACHE_CONFIG.LONG_CACHE,
        };
      case 'user':
        return {
          staleTime: CACHE_CONFIG.USER_DATA,
          gcTime: CACHE_CONFIG.DEFAULT_CACHE,
        };
      case 'product':
        return {
          staleTime: CACHE_CONFIG.PRODUCT_DATA,
          gcTime: CACHE_CONFIG.DEFAULT_CACHE,
        };
      case 'search':
        return {
          staleTime: CACHE_CONFIG.SEARCH_DATA,
          gcTime: CACHE_CONFIG.SHORT_CACHE,
        };
      case 'analytics':
        return {
          staleTime: CACHE_CONFIG.ANALYTICS_DATA,
          gcTime: CACHE_CONFIG.SHORT_CACHE,
        };
      default:
        return {
          staleTime: CACHE_CONFIG.PRODUCT_DATA,
          gcTime: CACHE_CONFIG.DEFAULT_CACHE,
        };
    }
  }, [cacheType]);

  // Enhanced query function with performance tracking
  const enhancedQueryFn = useCallback(async () => {
    const keyStr = JSON.stringify(queryKey);
    
    try {
      // Check if data exists in cache first
      const cachedData = queryClient.getQueryData(queryKey);
      
      if (cachedData && trackPerformance) {
        cachePerformance.trackCacheHit(keyStr);
      } else if (trackPerformance) {
        cachePerformance.trackCacheMiss(keyStr);
      }

      const result = await queryFn();
      
      console.log(`✅ Query success for ${keyStr}`);
      return result;
    } catch (error) {
      console.error(`❌ Query failed for ${keyStr}:`, error);
      throw error;
    }
  }, [queryKey, queryFn, queryClient, trackPerformance]);

  // Base query with cache configuration
  const cacheConfig = getCacheConfig();
  const query = useQuery({
    queryKey,
    queryFn: enhancedQueryFn,
    ...cacheConfig,
    ...queryOptions,
    // Enable background refetching if requested
    refetchInterval: backgroundRefresh ? CACHE_CONFIG.REFETCH_INTERVAL : false,
    refetchIntervalInBackground: backgroundRefresh,
  });

  // Prefetch related data for better UX
  useEffect(() => {
    if (query.isSuccess && query.data && optimisticKey) {
      // Store optimistic data for future use
      localStorage.setItem(`optimistic_${optimisticKey}`, JSON.stringify(query.data));
    }
  }, [query.isSuccess, query.data, optimisticKey]);

  // Return enhanced query with additional utilities
  return {
    ...query,
    // Cache utilities
    invalidate: () => queryClient.invalidateQueries({ queryKey }),
    refetch: () => queryClient.refetchQueries({ queryKey }),
    remove: () => queryClient.removeQueries({ queryKey }),
    
    // Performance info
    cacheHit: !!queryClient.getQueryData(queryKey),
    cacheAge: query.dataUpdatedAt ? Date.now() - query.dataUpdatedAt : 0,
    
    // Optimistic data
    getOptimisticData: () => {
      if (!optimisticKey) return null;
      try {
        const data = localStorage.getItem(`optimistic_${optimisticKey}`);
        return data ? JSON.parse(data) : null;
      } catch {
        return null;
      }
    },
  };
}

// Specialized hooks for common query patterns
export function useStaticQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: Omit<OptimizedQueryOptions<T>, 'cacheType'> = {}
) {
  return useOptimizedQuery(queryKey, queryFn, { ...options, cacheType: 'static' });
}

export function useUserQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: Omit<OptimizedQueryOptions<T>, 'cacheType'> = {}
) {
  return useOptimizedQuery(queryKey, queryFn, { ...options, cacheType: 'user' });
}

export function useProductQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: Omit<OptimizedQueryOptions<T>, 'cacheType'> = {}
) {
  return useOptimizedQuery(queryKey, queryFn, { ...options, cacheType: 'product' });
}

export function useSearchQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: Omit<OptimizedQueryOptions<T>, 'cacheType'> = {}
) {
  return useOptimizedQuery(queryKey, queryFn, { ...options, cacheType: 'search' });
}

export function useAnalyticsQuery<T>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
  options: Omit<OptimizedQueryOptions<T>, 'cacheType'> = {}
) {
  return useOptimizedQuery(queryKey, queryFn, { ...options, cacheType: 'analytics' });
}